// Terraform resource parser - state machine approach, no external dependencies

export interface TerraformAttribute {
  key: string;
  value: string;
  isS3Reference: boolean;
}

export interface TerraformResource {
  type: string;
  name: string;
  attributes: TerraformAttribute[];
  rawBlock: string;
  lineStart: number;
  lineEnd: number;
}

export interface TerraformParseResult {
  resources: TerraformResource[];
  errors: string[];
}

const S3_REFERENCE_PATTERNS = [
  'asset_directory',
  's3://',
  '.qcow2',
  '.ova',
  '.ovf',
  '.vmdk',
  '.iso',
  '.img',
  'content_path',
  'source_path',
  'image_path',
  'disk_image',
];

export const RANGEOS_RESOURCE_TYPES = [
  'rangeos_scenario',
  'rangeos_network',
  'rangeos_vm',
  'rangeos_container',
  'rangeos_traffic_profile',
  'rangeos_scoring',
  'rangeos_inject',
  'rangeos_dns_record',
  'rangeos_firewall_rule',
  'rangeos_nat_rule',
  'rangeos_route',
  'rangeos_subnet',
  'rangeos_volume',
  'rangeos_cloud_init',
  'rangeos_interface',
];

export const RESOURCE_KEY_FIELDS: Record<string, string[]> = {
  rangeos_scenario: ['display_name', 'description', 'asset_directory'],
  rangeos_network: ['display_name', 'cidr', 'gateway'],
  rangeos_vm: ['display_name', 'image', 'flavor', 'disk_image', 'content_path'],
  rangeos_container: ['display_name', 'image', 'content_path'],
  rangeos_traffic_profile: ['display_name', 'type', 'source_path'],
  rangeos_scoring: ['display_name', 'type'],
  rangeos_inject: ['display_name', 'type'],
  rangeos_subnet: ['display_name', 'cidr', 'gateway'],
  rangeos_volume: ['display_name', 'size', 'source_path'],
  rangeos_interface: ['network', 'ip_address'],
};

function isS3Reference(value: string): boolean {
  const lower = value.toLowerCase();
  return S3_REFERENCE_PATTERNS.some(p => lower.includes(p));
}

export function parseTerraformResources(content: string): TerraformParseResult {
  const lines = content.split('\n');
  const resources: TerraformResource[] = [];
  const errors: string[] = [];

  const resourcePattern = /^resource\s+"([^"]+)"\s+"([^"]+)"\s*\{/;

  let i = 0;
  while (i < lines.length) {
    const match = lines[i].match(resourcePattern);
    if (!match) {
      i++;
      continue;
    }

    const type = match[1];
    const name = match[2];
    const lineStart = i + 1; // 1-indexed

    // Find matching closing brace
    let depth = 1;
    let j = i + 1;
    let inString = false;
    let stringChar = '';

    while (j < lines.length && depth > 0) {
      const line = lines[j];
      for (let c = 0; c < line.length; c++) {
        const ch = line[c];
        const prev = c > 0 ? line[c - 1] : '';

        if (inString) {
          if (ch === stringChar && prev !== '\\') {
            inString = false;
          }
          continue;
        }

        if (ch === '"' || ch === "'") {
          inString = true;
          stringChar = ch;
        } else if (ch === '{') {
          depth++;
        } else if (ch === '}') {
          depth--;
          if (depth === 0) break;
        }
      }
      if (depth > 0) j++;
    }

    if (depth !== 0) {
      errors.push(`Unmatched brace for resource "${type}" "${name}" starting at line ${lineStart}`);
      i++;
      continue;
    }

    const lineEnd = j + 1; // 1-indexed
    const blockLines = lines.slice(i, j + 1);
    const rawBlock = blockLines.join('\n');

    // Extract top-level attributes (depth 1 only)
    const attributes = extractAttributes(lines.slice(i + 1, j));

    resources.push({ type, name, attributes, rawBlock, lineStart, lineEnd });
    i = j + 1;
  }

  return { resources, errors };
}

function extractAttributes(blockLines: string[]): TerraformAttribute[] {
  const attrs: TerraformAttribute[] = [];
  const attrPattern = /^\s*(\w+)\s*=\s*(.+)/;
  let depth = 0;

  for (const line of blockLines) {
    // Track nested block depth
    for (const ch of line) {
      if (ch === '{') depth++;
      if (ch === '}') depth--;
    }

    // Only extract attributes at top level (depth 0 before this line's braces)
    if (depth > 0) continue;

    // Skip lines that are just closing braces or comments
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//') || trimmed === '}') continue;

    const match = trimmed.match(attrPattern);
    if (!match) continue;

    const key = match[1];
    let value = match[2].trim();

    // Strip trailing comments
    // Be careful not to strip # inside strings
    if (!value.startsWith('"')) {
      const commentIdx = value.indexOf('#');
      if (commentIdx > 0) value = value.substring(0, commentIdx).trim();
    }

    // Remove surrounding quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }

    // Skip block-opening attributes (value ends with {)
    if (value.endsWith('{')) continue;

    attrs.push({
      key,
      value,
      isS3Reference: isS3Reference(key) || isS3Reference(value),
    });
  }

  return attrs;
}
